// src/pages/AdminDashboard.tsx
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Building2, UserPlus, Users, ScrollText, BarChart3, ClipboardList, User, LogOut, type LucideIcon } from 'lucide-react';
import { authService } from 'services/authService';
import { logout } from 'store/slices/authSlice';
import { RootState } from 'store';

const TABS: { id: string; label: string; icon: LucideIcon; subtitle: string }[] = [
  { id: 'roles', label: 'Hierarchy', icon: Building2, subtitle: 'Manage the officer hierarchy and department permissions' },
  { id: 'create', label: 'Add User', icon: UserPlus, subtitle: 'Provision a new user account' },
  { id: 'users', label: 'All Users', icon: Users, subtitle: 'Manage all provisioned accounts' },
  { id: 'audit', label: 'Audit Log', icon: ScrollText, subtitle: 'Org-wide compliance log of every workflow/role/admin change' },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, subtitle: 'Org-wide task analytics and trends' },
];

export default function AdminDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const adminUser = useSelector((s: RootState) => s.auth.user);
  const tab = location.pathname.split('/')[2] ?? 'users';
  const currentTab = TABS.find(t => t.id === tab);

  async function handleLogout() {
    try { await authService.logout(); } catch { /* ignore */ }
    dispatch(logout());
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-[var(--om-app)] text-[var(--om-text)] font-sans">
      <header className="sticky top-0 z-20 h-16 bg-[var(--om-panel)] border-b border-[var(--om-border)] px-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-800 to-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
            <ClipboardList className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <span className="font-black text-[var(--om-text)] tracking-wide text-sm">TODO</span>
            <span className="ml-2 px-2 py-0.5 bg-red-500/20 text-red-400 text-[11px] font-bold rounded-full uppercase tracking-wider">
              Admin
            </span>
          </div>
        </div>

        <p className="flex items-center gap-1.5 text-base font-semibold text-[var(--om-text)]">
          <User className="w-4 h-4" strokeWidth={2} />
          {adminUser?.email}
        </p>
      </header>

      <div className="flex items-start">

        <aside className="w-1/5 shrink-0 sticky top-16 self-start h-[calc(100dvh-4rem)] bg-[var(--om-card)] border-r border-[var(--om-border)] p-1.5 flex flex-col gap-0.5">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => navigate(`/admin/${t.id}`)}
              className={`flex items-center gap-2 px-2.5 py-2.5 rounded-lg text-sm font-bold text-left transition-all ${
                tab === t.id ? 'bg-blue-500 text-white' : 'text-[var(--om-text)] hover:text-[var(--om-text)] hover:bg-[var(--om-overlay-05)]'
              }`}
            >
              <t.icon className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
              {t.label}
            </button>
          ))}

          <button
            onClick={handleLogout}
            className="mt-auto flex items-center gap-2 px-2.5 py-2.5 rounded-lg text-sm font-bold text-left bg-[var(--om-overlay-05)] border border-[var(--om-overlay-08)] text-[var(--om-text)] hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
            Logout
          </button>
        </aside>

        <main className="flex-1 min-w-0 px-6 py-8">
          <div className="mb-6">
            <h1 className="flex items-center gap-2 text-2xl font-black text-[var(--om-text)] mb-1">
              {currentTab && <currentTab.icon className="w-6 h-6" strokeWidth={2} />} {currentTab?.label ?? 'Admin Dashboard'}
            </h1>
            <p className="text-sm text-[var(--om-text-dim)]">{currentTab?.subtitle ?? 'Manage users, provisioning, and the officer hierarchy'}</p>
          </div>

          <Outlet />
        </main>
      </div>
    </div>
  );
}
